import React from "react";
import { Box, Typography } from "@mui/material";
import TrendChart from "./TrendChart";
import { getTopicColor } from "./topicOrder";

// Same default labels DetailView.js uses — kept local here too (existing
// codebase convention: ConversationsV3.js and DetailView.js each already
// carry their own copy of this map/humanizer rather than a shared helper).
//
// purchaseEnablers/purchaseBarriers and customerComplaints/customerPraises are
// intentionally absent — the trend endpoint (getConversationMonitoringTrend)
// never emits those 4 keys individually anymore, it combines each pair into
// one net-balance line (purchaseEnablersBarriers / customerComplaintsPraises)
// pooling both topics' items into the same weighted-average formula. This is
// trend-only — DetailView.js keeps its own copy of this map unchanged, since
// the single-run breakdown still shows all 9 topics separately.
const DEFAULT_TOPIC_LABELS = {
  discussionTopics: "Discussion Topics",
  purchaseEnablersBarriers: "Purchase Enablers vs Barriers",
  keyQuestions: "Key Questions",
  customerNeeds: "Customer Needs",
  customerPainPoints: "Customer Pain Points",
  customerComplaintsPraises: "Customer Complaints vs Praises",
  customerEmergingTrends: "Emerging Trends",
};

// Canonical display order for the trend chart's lines — matches
// DetailView.js's DEFAULT_TOPIC_ORDER except customerPainPoints is
// intentionally excluded here: it's still shown in the Topic Bucket
// Breakdown, just not plotted as a trend line. Any topic key not in this
// list (dynamic/per-customer topics) is appended after, in whatever order
// the backend returned it.
const DEFAULT_TOPIC_ORDER = [
  "discussionTopics",
  "keyQuestions",
  "purchaseEnablersBarriers",
  "customerComplaintsPraises",
  "customerNeeds",
  "customerEmergingTrends",
];

function humanizeTopicKey(key) {
  if (DEFAULT_TOPIC_LABELS[key]) return DEFAULT_TOPIC_LABELS[key];
  // Dynamic (per-customer) topic keys look like "EmployerKeyDiscussionTopicsV1"
  return key
    .replace(/V\d+$/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Sentiment trend section — a plain line chart. `trend` comes from GET
 * /conversation-monitoring/trend: { dates, topics: [{topicKey, scores}], overall }.
 * Every topic the backend returns is plotted (in DEFAULT_TOPIC_ORDER, with
 * customerPainPoints deliberately excluded from this chart only — it's still
 * in the Topic Bucket Breakdown), plus the server-computed "Overall" aggregate.
 * No filter bar, no baseline/recent summary
 * table, no end-of-line value labels — score and topic name only appear on
 * hover, via each dot's tooltip. Same layout as the original mock; only the
 * data source changed.
 */
export default function TrendsView({ trend }) {
  const dates = trend?.dates || [];
  const overall = trend?.overall || [];
  const overallExplanations = trend?.overallExplanations || [];

  // Reorder to DEFAULT_TOPIC_ORDER, dropping customerPainPoints entirely;
  // any topic not in that list (dynamic/per-customer ones) keeps its
  // original relative order, appended after the known defaults.
  const allTopics = trend?.topics || [];
  const topics = [
    ...DEFAULT_TOPIC_ORDER.map((key) => allTopics.find((t) => t.topicKey === key)).filter(Boolean),
    ...allTopics.filter((t) => !DEFAULT_TOPIC_ORDER.includes(t.topicKey) && t.topicKey !== "customerPainPoints"),
  ];

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "8px", boxShadow: "0px 3.5px 5.5px 0px rgba(0,0,0,0.05)", p: "22px 26px" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1a2027", mb: 0.25 }}>Brand Conversations Sentiment Trend</Typography>
      <Typography sx={{ fontSize: 12.5, color: "#667085", mb: 2 }}>
        Scale &minus;3 (very negative) to +3 (very positive)
      </Typography>
      {dates.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: "#667085", py: 2 }}>Not enough completed runs yet to show a trend.</Typography>
      ) : (
        <TrendChart
          months={dates.map(formatDateLabel)}
          series={[
            ...topics.map((t) => ({
              key: t.topicKey,
              label: humanizeTopicKey(t.topicKey),
              color: getTopicColor(t.topicKey),
              points: t.scores,
              explanations: t.explanations || [],
            })),
            { key: "overall", label: "Overall (all topics)", color: "#0b0b0b", points: overall, explanations: overallExplanations, isOverall: true },
          ]}
        />
      )}
    </Box>
  );
}
